import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useArtists } from '../../contexts/ArtistsContext';
import {
  addTestArtist,
  fetchTestArtists,
  removeTestArtist,
} from '../../services/artistService';

const PageContainer = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  margin-bottom: var(--space-lg);
  padding-bottom: var(--space-md);
  border-bottom: 1px solid var(--color-border);
`;

const Title = styled.h1`
  font-size: var(--font-size-3xl);
  font-family: var(--font-serif);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  margin: 0;
`;

const Subtitle = styled.p`
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  margin: 0;
  max-width: 760px;
`;

const SummaryRow = styled.div`
  display: flex;
  gap: var(--space-sm);
  align-items: center;
  flex-wrap: wrap;
`;

const SummaryBadge = styled.span`
  background: var(--color-surface);
  color: var(--color-accent);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  padding: var(--space-xs) var(--space-sm);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
`;

const RefreshButton = styled.button`
  border: 1px solid var(--color-border);
  background: white;
  color: var(--color-text-secondary);
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);

  &:hover {
    border-color: var(--color-accent);
    color: var(--color-primary);
  }
`;

const AddPanel = styled.form`
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  padding: var(--space-md);
  margin-bottom: var(--space-lg);
  display: grid;
  gap: var(--space-sm);
`;

const AddPanelHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xxs);
`;

const PanelTitle = styled.h2`
  color: var(--color-primary);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  margin: 0;
`;

const PanelCopy = styled.p`
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  margin: 0;
`;

const AddControls = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 0.8fr) minmax(260px, 1.4fr) auto;
  gap: var(--space-sm);
  align-items: end;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: var(--space-xxs);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-light);
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const SearchInput = styled.input`
  height: 46px;
  padding: var(--space-xs) var(--space-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  background: var(--color-surface);
  color: var(--color-text);

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 4px rgba(0, 24, 206, 0.08);
  }
`;

const Select = styled.select`
  height: 46px;
  padding: var(--space-xs) var(--space-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  background: white;
  color: var(--color-text);

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 4px rgba(0, 24, 206, 0.08);
  }
`;

const PrimaryButton = styled.button`
  min-height: 46px;
  border: 1px solid var(--color-periwinkle);
  background: var(--color-periwinkle);
  color: white;
  padding: var(--space-xs) var(--space-md);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;

  &:hover {
    background: var(--color-accent);
    border-color: var(--color-accent);
  }

  &:disabled {
    background: var(--color-primary-light);
    border-color: var(--color-border-light);
    color: var(--color-text-light);
  }
`;

const SecondaryButton = styled.button`
  border: 1px solid var(--color-border);
  background: white;
  color: var(--color-text-secondary);
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);

  &:hover {
    border-color: var(--color-error);
    color: var(--color-error);
  }
`;

const FeedbackMessage = styled.p.withConfig({
  shouldForwardProp: (prop) => prop !== 'error',
})`
  margin: 0;
  font-size: var(--font-size-sm);
  color: ${props => props.error ? 'var(--color-error)' : 'var(--color-success)'};
`;

const TableTools = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-sm);
  margin-bottom: var(--space-sm);
  flex-wrap: wrap;
`;

const TableWrapper = styled.div`
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  overflow-x: auto;
`;

const ArtistsTable = styled.table`
  width: 100%;
  min-width: 880px;
  border-collapse: collapse;
`;

const TableHeadCell = styled.th`
  text-align: left;
  padding: var(--space-sm) var(--space-md);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  background: var(--color-primary-lighter);
  border-bottom: 1px solid var(--color-border);
  white-space: nowrap;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: rgba(247, 247, 243, 0.72);
  }

  &:hover {
    background: rgba(130, 172, 216, 0.08);
  }
`;

const TableCell = styled.td`
  padding: var(--space-md);
  font-size: var(--font-size-md);
  color: var(--color-text);
  border-bottom: 1px solid var(--color-border-light);
  vertical-align: top;
`;

const ArtistNameLink = styled(Link)`
  color: var(--color-primary);
  font-weight: var(--font-weight-semibold);
  text-decoration: none;

  &:hover {
    color: var(--color-accent);
  }
`;

const MutedText = styled.span`
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
`;

const StatusBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'active',
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 86px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.02em;
  background: ${props => props.active ? 'rgba(130, 172, 216, 0.16)' : 'rgba(93, 64, 34, 0.08)'};
  color: ${props => props.active ? 'var(--color-accent)' : 'var(--color-text-muted)'};
`;

const EmptyState = styled.div`
  padding: var(--space-xl);
  text-align: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
`;

const ErrorBox = styled.div`
  padding: var(--space-lg);
  border: 1px solid var(--color-error);
  border-radius: var(--border-radius-md);
  background: rgba(180, 35, 24, 0.08);
  color: var(--color-error);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-md);
`;

const formatDate = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value.toDate === 'function') {
    return value.toDate().toLocaleString();
  }

  if (value instanceof Date) {
    return value.toLocaleString();
  }

  if (typeof value === 'number') {
    return new Date(value).toLocaleString();
  }

  if (typeof value === 'string') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleString();
    }
  }

  if (value.seconds) {
    return new Date(value.seconds * 1000).toLocaleString();
  }

  return null;
};

const optionLabel = (artist) =>
  `${artist.user_display_name}${artist.user_email ? ` (${artist.user_email})` : ''}`;

const TestArtists = () => {
  const { artists, loadingArtists, refreshArtists } = useArtists();
  const [testArtists, setTestArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [candidateSearchTerm, setCandidateSearchTerm] = useState('');
  const [selectedArtistId, setSelectedArtistId] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const loadTestArtists = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchTestArtists();
      setTestArtists(data);
    } catch (err) {
      setError(err.message || 'Unable to load test artists.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTestArtists();
  }, [loadTestArtists]);

  const testArtistIds = useMemo(
    () => new Set(testArtists.map((artist) => artist.artistId).filter(Boolean)),
    [testArtists]
  );

  const availableArtists = useMemo(() => {
    const normalizedTerm = candidateSearchTerm.trim().toLowerCase();

    return artists
      .filter((artist) => !artist.isTestArtist && !testArtistIds.has(artist.id))
      .filter((artist) => {
        if (!normalizedTerm) {
          return true;
        }

        return (
          artist.user_display_name.toLowerCase().includes(normalizedTerm) ||
          (artist.user_email && artist.user_email.toLowerCase().includes(normalizedTerm)) ||
          (artist.user_id && artist.user_id.toLowerCase().includes(normalizedTerm)) ||
          (artist.id && artist.id.toLowerCase().includes(normalizedTerm))
        );
      })
      .sort((a, b) => a.user_display_name.localeCompare(b.user_display_name));
  }, [artists, candidateSearchTerm, testArtistIds]);

  const filteredTestArtists = useMemo(() => {
    const normalizedTerm = memberSearchTerm.trim().toLowerCase();

    if (!normalizedTerm) {
      return testArtists;
    }

    return testArtists.filter((artist) => (
      artist.artistName.toLowerCase().includes(normalizedTerm) ||
      (artist.email && artist.email.toLowerCase().includes(normalizedTerm)) ||
      (artist.artistId && artist.artistId.toLowerCase().includes(normalizedTerm))
    ));
  }, [testArtists, memberSearchTerm]);

  const handleRefresh = async () => {
    setMessage(null);
    await Promise.all([loadTestArtists(), refreshArtists()]);
  };

  const handleAdd = async (event) => {
    event.preventDefault();

    if (!selectedArtistId || actionLoading) {
      return;
    }

    const selectedArtist = artists.find((artist) => artist.id === selectedArtistId);

    try {
      setActionLoading(`add:${selectedArtistId}`);
      setError(null);
      setMessage(null);
      await addTestArtist(selectedArtistId);
      setSelectedArtistId('');
      setCandidateSearchTerm('');
      await Promise.all([loadTestArtists(), refreshArtists()]);
      setMessage(`${selectedArtist?.user_display_name || 'Artist'} added to the test group.`);
    } catch (err) {
      setError(err.message || 'Unable to add artist to the test group.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (artist) => {
    if (!artist?.artistId || actionLoading) {
      return;
    }

    const confirmed = typeof window !== 'undefined'
      ? window.confirm(`Remove ${artist.artistName || 'this artist'} from the test group? Free test access ends immediately.`)
      : true;

    if (!confirmed) {
      return;
    }

    try {
      setActionLoading(`remove:${artist.artistId}`);
      setError(null);
      setMessage(null);
      await removeTestArtist(artist.artistId);
      await Promise.all([loadTestArtists(), refreshArtists()]);
      setMessage(`${artist.artistName || 'Artist'} removed from the test group.`);
    } catch (err) {
      setError(err.message || 'Unable to remove artist from the test group.');
    } finally {
      setActionLoading(null);
    }
  };

  const addDisabled =
    !selectedArtistId ||
    actionLoading !== null ||
    loadingArtists ||
    availableArtists.length === 0;

  return (
    <PageContainer>
      <PageHeader>
        <Title>Test Artists</Title>
        <Subtitle>
          Manage feedback-programme access for existing artists. Test artists receive subscription-equivalent access, platform commission is waived, and adding a subscribed artist may schedule their Stripe subscription to cancel at period end.
        </Subtitle>
        <SummaryRow>
          <SummaryBadge>{testArtists.length} in test group</SummaryBadge>
          {memberSearchTerm.trim() && (
            <SummaryBadge>{filteredTestArtists.length} showing</SummaryBadge>
          )}
          <RefreshButton type="button" onClick={handleRefresh} disabled={loading || loadingArtists || actionLoading !== null}>
            {loading || loadingArtists ? 'Refreshing...' : 'Refresh'}
          </RefreshButton>
        </SummaryRow>
      </PageHeader>

      <AddPanel onSubmit={handleAdd}>
        <AddPanelHeader>
          <PanelTitle>Add an existing artist</PanelTitle>
          <PanelCopy>
            Select an artist profile that already exists in the admin artist list. New artist invitations stay in the invite flow.
          </PanelCopy>
        </AddPanelHeader>
        <AddControls>
          <Field>
            Search artists
            <SearchInput
              type="search"
              value={candidateSearchTerm}
              onChange={(event) => setCandidateSearchTerm(event.target.value)}
              placeholder="Name, email, or ID"
              aria-label="Search artists to add to the test group"
            />
          </Field>
          <Field>
            Artist
            <Select
              value={selectedArtistId}
              onChange={(event) => setSelectedArtistId(event.target.value)}
              aria-label="Select artist to add to the test group"
              disabled={loadingArtists || actionLoading !== null || availableArtists.length === 0}
            >
              <option value="">
                {availableArtists.length === 0 ? 'No eligible artists found' : 'Select an artist'}
              </option>
              {availableArtists.map((artist) => (
                <option key={artist.id} value={artist.id}>
                  {optionLabel(artist)}
                </option>
              ))}
            </Select>
          </Field>
          <PrimaryButton type="submit" disabled={addDisabled}>
            {actionLoading?.startsWith('add:') ? 'Adding...' : 'Add to test group'}
          </PrimaryButton>
        </AddControls>
      </AddPanel>

      {(error || message) && (
        <FeedbackMessage error={Boolean(error)} role={error ? 'alert' : 'status'}>
          {error || message}
        </FeedbackMessage>
      )}

      <TableTools>
        <SearchInput
          type="search"
          value={memberSearchTerm}
          onChange={(event) => setMemberSearchTerm(event.target.value)}
          placeholder="Search test artists"
          aria-label="Search test artists"
        />
      </TableTools>

      {error && testArtists.length === 0 ? (
        <ErrorBox role="alert">
          <span>{error}</span>
          <RefreshButton type="button" onClick={handleRefresh}>Try again</RefreshButton>
        </ErrorBox>
      ) : (
        <TableWrapper aria-busy={loading}>
          {loading ? (
            <EmptyState>Loading test artists...</EmptyState>
          ) : testArtists.length === 0 ? (
            <EmptyState>No artists are in the test group.</EmptyState>
          ) : filteredTestArtists.length === 0 ? (
            <EmptyState>No test artists match your search.</EmptyState>
          ) : (
            <ArtistsTable>
              <thead>
                <tr>
                  <TableHeadCell scope="col">Artist</TableHeadCell>
                  <TableHeadCell scope="col">Email</TableHeadCell>
                  <TableHeadCell scope="col">Account</TableHeadCell>
                  <TableHeadCell scope="col">Payouts</TableHeadCell>
                  <TableHeadCell scope="col">Added</TableHeadCell>
                  <TableHeadCell scope="col">Action</TableHeadCell>
                </tr>
              </thead>
              <tbody>
                {filteredTestArtists.map((artist) => (
                  <TableRow key={artist.artistId}>
                    <TableCell>
                      <ArtistNameLink to={`/artists/existing/${artist.artistId}`}>
                        {artist.artistName}
                      </ArtistNameLink>
                      <br />
                      <MutedText>{artist.artistId}</MutedText>
                    </TableCell>
                    <TableCell>
                      {artist.email ? (
                        <a href={`mailto:${artist.email}`}>{artist.email}</a>
                      ) : (
                        <MutedText>Not provided</MutedText>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge active={artist.account_active}>
                        {artist.account_active ? 'Active' : 'Inactive'}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <StatusBadge active={artist.connectReady}>
                        {artist.connectReady ? 'Ready' : 'Not ready'}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      {formatDate(artist.createdAt) || <MutedText>Unknown</MutedText>}
                    </TableCell>
                    <TableCell>
                      <SecondaryButton
                        type="button"
                        onClick={() => handleRemove(artist)}
                        disabled={actionLoading !== null}
                      >
                        {actionLoading === `remove:${artist.artistId}` ? 'Removing...' : 'Remove'}
                      </SecondaryButton>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </ArtistsTable>
          )}
        </TableWrapper>
      )}
    </PageContainer>
  );
};

export default TestArtists;
